import React from 'react';
import { BsX, BsEye, BsLink45Deg, BsCalendar3, BsPersonWorkspace, BsPersonStanding, BsPersonStandingDress, BsArrowLeftRight, BsCheckCircle, BsShieldLock } from 'react-icons/bs';
import { BiPencil } from 'react-icons/bi';

interface HelpDrawerProcessosProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string }> = ({ icon, label, description, bgColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: '1px solid #ccc',
      backgroundColor: bgColor,
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerProcessos: React.FC<HelpDrawerProcessosProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Processos</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: '0 0 8px 0' }}>
              Lista todos os processos. Cada processo agrupa editais, candidatos e bancas de heteroidentificação.
            </p>
          </Section>

          <Section title="Filtros">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Edital:</strong> Nome do edital vinculado.</li>
              <li><strong>Identificador:</strong> Código único do processo.</li>
              <li><strong>Responsável:</strong> Nome do responsável.</li>
              <li><strong>Unidade:</strong> Instituto, faculdade, etc.</li>
              <li><strong>Status:</strong> Ativos, Cancelados ou Finalizados.</li>
            </ul>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
              Filtros aplicados automaticamente conforme você digita.
            </p>
          </Section>

          <Section title="Colunas da Tabela">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Editais:</strong> Clique no número para ver todos.</li>
              <li><strong>Identificador:</strong> Passe o mouse para ver contas de serviço.</li>
              <li><strong>Unidade:</strong> Unidade responsável.</li>
              <li><strong>Status:</strong> Clique para ver a descrição.</li>
            </ul>
          </Section>

          <Section title="Candidatos">
            <ActionItem icon={<><BsPersonStandingDress style={{ marginRight: '-2px' }} /><BsPersonStanding /></>} label="Candidatos" description="Abre a lista de candidatos do processo." bgColor="#f8f9fa" />
            <ActionItem icon={<BsArrowLeftRight />} label="Integração" description="Abre ou fecha a integração de candidatos com sistemas externos." bgColor="#d1fae5" />
          </Section>

          <Section title="Ações">
            <ActionItem icon={<BiPencil />} label="Editar" description="Edita nome e dados do processo." bgColor="#e3f2fd" />
            <ActionItem icon={<BsLink45Deg />} label="Sala de Espera" description="Define o link da sala de espera para candidatos." bgColor="#f5f5f5" />
            <ActionItem icon={<BsCalendar3 />} label="Cronograma" description="Define o cronograma de atividades do processo." bgColor="#fff3cd" />
            <ActionItem icon={<BsShieldLock />} label="Admin" description="Ações administrativas (apenas administradores)." bgColor="#e8daef" />
          </Section>

          <Section title="Bancas e Finalização">
            <ActionItem icon={<BsPersonWorkspace />} label="Bancas" description="Acessa a gestão de bancas do processo." bgColor="#f0e68c" />
            <ActionItem icon={<BsCheckCircle />} label="Finalizar" description="Finaliza o processo. Aparece quando todas as bancas estão concluídas." bgColor="#28a745" />
          </Section>

          <Section title="Visualização Detalhada">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <BsEye style={{ fontSize: '16px', flexShrink: 0, marginTop: '2px' }} />
              <span>
                Clique no ícone do olho para alternar. A visualização detalhada mostra Responsável e Data de Criação.
              </span>
            </div>
          </Section>

          <Section title="Fluxo do Processo">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
              {[
                { status: 'Ativo', desc: 'Em andamento, bancas podem ser criadas.' },
                { status: 'Finalizado', desc: 'Bancas concluídas, resultados publicados.' },
                { status: 'Cancelado', desc: 'Cancelado por usuário DEDH.' },
              ].map(({ status, desc }) => (
                <div key={status} style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ minWidth: '80px' }}>{status}:</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Use "+ Novo Processo" no canto inferior direito para criar.</li>
              <li>Processos com integração SIGA são destacados na tabela.</li>
              <li>Clique em "Bancas" para ir direto à gestão de bancas.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerProcessos;
